import React, { useEffect, useState } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Activity, CheckCircle, XCircle, AlertTriangle, Loader2, RefreshCw,
  Server, Globe, Shield, Database, Mail, Webhook, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const StatusDot = ({ ok, label, detail }) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0">
    <div className={`mt-0.5 w-3 h-3 rounded-full shrink-0 ${ok === true ? 'bg-emerald-400' : ok === false ? 'bg-red-400' : 'bg-amber-400'}`} />
    <div className="flex-1 min-w-0">
      <p className={`text-sm font-medium ${ok === true ? 'text-gray-900' : ok === false ? 'text-red-700' : 'text-amber-700'}`}>{label}</p>
      {detail && <p className="text-xs text-gray-500 mt-0.5 break-all">{detail}</p>}
    </div>
    {ok === true ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" /> :
     ok === false ? <XCircle className="w-4 h-4 text-red-500 shrink-0" /> :
     <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
  </div>
);

export default function SystemStatusPage() {
  const [configStatus, setConfigStatus] = useState(null);
  const [txHealth, setTxHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const [cs, th] = await Promise.all([
        superAdminAPI.getConfigStatus(),
        superAdminAPI.getTransactionHealth().catch(() => null),
      ]);
      setConfigStatus(cs);
      setTxHealth(th);
      if (isRefresh) toast.success('Status refreshed');
    } catch {
      toast.error('Failed to load status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-pink-500" />
      </div>
    );
  }

  const cs = configStatus;
  const hasErrors = txHealth?.recent_errors_count > 0;

  // Overall health
  const allHealthy = cs?.proxy_active && cs?.ip_matches_fixie && cs?.url_source === 'DATABASE' && !hasErrors;

  return (
    <div className="space-y-6" data-testid="system-status-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">System Status</h1>
          <p className="text-sm text-gray-500">Real-time health of all connected systems</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing}
          className="h-8 text-xs" data-testid="refresh-status-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overall Status */}
      <Card className={`border-2 ${allHealthy ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/30'}`} data-testid="overall-status">
        <CardContent className="p-5 flex items-center gap-4">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${allHealthy ? 'bg-emerald-100' : 'bg-amber-100'}`}>
            {allHealthy ? <CheckCircle className="w-7 h-7 text-emerald-600" /> : <AlertTriangle className="w-7 h-7 text-amber-600" />}
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900">{allHealthy ? 'All Systems Operational' : 'Attention Needed'}</p>
            <p className="text-sm text-gray-500">Last checked: {new Date().toLocaleString()}</p>
          </div>
        </CardContent>
      </Card>

      {/* Status Checks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-semibold text-gray-700">Banking API (Assets MFB)</p>
            </div>
            <StatusDot ok={!!cs?.active_url} label="Base URL configured" detail={cs?.active_url} />
            <StatusDot ok={cs?.url_source === 'DATABASE'} label="URL sourced from database"
              detail={cs?.url_source !== 'DATABASE' ? `Currently from: ${cs?.url_source}` : 'Highest priority source active'} />
            <StatusDot ok={cs?.environment === 'PRODUCTION' ? cs?.active_url?.includes('prod') : true}
              label={`URL matches ${cs?.environment} environment`} />
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-semibold text-gray-700">Egress Proxy (Fixie)</p>
            </div>
            <StatusDot ok={cs?.proxy_active} label="Proxy active" detail={cs?.proxy_url || 'No proxy configured'} />
            <StatusDot ok={cs?.ip_matches_fixie}
              label={cs?.ip_matches_fixie ? `IP matches Fixie (${cs?.fixie_environment})` : 'Outbound IP does not match Fixie'}
              detail={`Current IP: ${cs?.outbound_ip}`} />
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-semibold text-gray-700">Transaction Health</p>
            </div>
            <StatusDot ok={!hasErrors} label={hasErrors ? `${txHealth?.recent_errors_count} recent errors` : 'No recent errors'} />
            {txHealth?.unresolved_count > 0 && (
              <StatusDot ok={false} label={`${txHealth.unresolved_count} unresolved errors`} />
            )}
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Server className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-semibold text-gray-700">Infrastructure</p>
            </div>
            <StatusDot ok={true} label="Application server running" />
            <StatusDot ok={true} label="Database connected" />
            <StatusDot ok={cs?.environment === 'PRODUCTION' || cs?.environment === 'STAGING'}
              label={`Environment: ${cs?.environment}`} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
